// Контракт API пульта (задача D10): разбор диалога — шапка, лента (сообщения, ходы с «Путём», пропуски) и правая колонка.
//
// `GET /api/v1/dialogs/:id?turn=` (любой вошедший) → `DialogView`: шапка и лента; заодно отмечает диалог «просмотрен» для
// этого сотрудника (так «Просмотр» остаётся только с GET). `GET /api/v1/dialogs/:id/summary` → `DialogSummary` — правая
// колонка отдельным запросом: ошибка колонки не роняет ленту (UX §3).
//
// Для роли не admin сервер сам ставит маску на телефон («+7 ••• ••• 12 34» — видны 4 последние цифры), в тексты сообщений и
// клиента, поля лида и «Путь»; ID трассы и оценки в ответе нет (`traceId: null`, `review: null`).
//
// Словари — в этом файле (тексты UX §3 и §6, п. 15, дословно); пульт берёт подписи отсюда. Перечисления ключей — копии
// серверных: `LeadKey` (C1-7), источники полей (C1-8), шаги «Пути» (C1-13), шаги идущего хода (C1-0), инструменты (C1-10),
// вердикты стоп-листа (план B), коды нарушений проверки (A12, C1-12); совпадение держит `tests/console/dialog-view.test.ts`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { FxSchema, ModeSchema, MomentSchema, StatusToneSchema, type StatusTone } from './common.js';
import { OutcomeSchema } from './dialogs.js';
import { JudgeAttemptViewSchema } from './eval-judges.js';
import { TurnReviewViewSchema } from './review.js';
import { GateReasonSchema } from './why.js';

/** Узкий неразрывный пробел между разрядами. */
const THIN = ' ';

/** Текст 404: диалога у Анны нет. */
export const DIALOG_NOT_FOUND_RU = 'Такого диалога у Анны нет: она видит только чаты отдела Квалификация после запуска';

// ───────────────────────────── поля лида ─────────────────────────────

/** Смысловые ключи полей лида (C1-7), по порядку таблицы; `phone` — телефон клиента, его находит код (P1, Ruling 121). */
export const LEAD_KEYS = [
  'product_name',
  'product_kind',
  'product_link',
  'service_kind',
  'service_scope',
  'quantity',
  'unit_price',
  'budget_rub',
  'purchase_sum',
  'weight_kg',
  'volume_m3',
  'has_supplier',
  'has_goods',
  'city',
  'purchase_term',
  'has_business',
  'entity_type',
  'purchase_purpose',
  'company_name',
  'business_notes',
  'client_name',
  'phone',
] as const;
export const LeadKeySchema = z.enum(LEAD_KEYS);
export type LeadKey = z.infer<typeof LeadKeySchema>;

export const LEAD_KEY_RU: Readonly<Record<LeadKey, string>> = {
  product_name: 'Товар',
  product_kind: 'Вид товара',
  product_link: 'Ссылка на товар',
  service_kind: 'Вид услуги',
  service_scope: 'Выкуп или только доставка',
  quantity: 'Количество',
  unit_price: 'Цена за штуку',
  budget_rub: 'Бюджет',
  purchase_sum: 'Сумма закупки',
  weight_kg: 'Вес',
  volume_m3: 'Объём',
  has_supplier: 'Есть поставщик',
  has_goods: 'Товар уже есть',
  city: 'Город',
  purchase_term: 'Срок закупки',
  has_business: 'Бизнес',
  entity_type: 'Лицо',
  purchase_purpose: 'Цель закупки',
  company_name: 'Компания',
  business_notes: 'Особенности бизнеса',
  client_name: 'Имя клиента',
  phone: 'Телефон',
};

/** Откуда Анна узнала значение поля (C1-8; `from_file` — прочитанный файл клиента, M4). Копия `LEAD_SOURCES` сервера. */
export const LEAD_SOURCES = ['client_said', 'from_link', 'from_photo', 'from_crm', 'computed', 'human', 'from_file'] as const;
export const LeadSourceSchema = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof LeadSourceSchema>;

/** Источники полей словами (UX §3). */
export const LEAD_SOURCE_RU: Readonly<Record<LeadSource, string>> = {
  client_said: 'клиент',
  from_link: 'ссылка',
  from_photo: 'фото',
  from_crm: 'Битрикс',
  computed: 'расчёт',
  human: 'человек',
  from_file: 'файл',
};

/**
 * Чего не хватает лиду (решение кода, A11) — словами. Коды плана Q (Q5): сумма партии, ИП или ООО, услуга, объём или вес
 * груза при «только доставка» (09.10.2026), город, телефон; прежние `volume_or_budget` и `purpose` — для ходов, записанных до Q5.
 */
export const MISSING_RU: Readonly<Record<string, string>> = {
  product: 'что за товар (ссылка или название)',
  budget: 'на какую сумму примерно партия',
  business: 'есть ли ИП или ООО',
  service: 'выкуп с доставкой или только доставка',
  volume: 'объём или вес груза',
  city: 'город доставки',
  phone: 'номер телефона',
  volume_or_budget: 'объём или сумма партии',
  purpose: 'закупка для бизнеса или для себя',
};

// ───────────────────────────── «Путь» хода ─────────────────────────────

/** Шаги «Пути» (C1-13, `PathStep`). */
export const PATH_STEPS = ['gate', 'prepare', 'analyze', 'decide', 'context', 'reply', 'checks', 'pacing', 'actions', 'send', 'error'] as const;
export const PathStepSchema = z.enum(PATH_STEPS);
export type PathStepName = z.infer<typeof PathStepSchema>;

/** Глаголы вместо названий моделей (UX §6, п. 15): так подписаны группы «Пути». */
export const PATH_STEP_VERB_RU: Readonly<Record<PathStepName, string>> = {
  gate: 'Ворота',
  prepare: 'Подготовила',
  analyze: 'Поняла',
  decide: 'Решила',
  context: 'Опиралась',
  reply: 'Написала',
  checks: 'Проверила',
  pacing: 'Темп',
  actions: 'Предложила',
  send: 'Перед отправкой',
  error: 'Ошибка',
};

/** Шаги идущего хода (`turns.progress`, C1-0). */
export const PROGRESS_STEPS = ['prepare', 'analyze', 'decide', 'reply', 'checks', 'pacing'] as const;
export const PROGRESS_RU: Readonly<Record<(typeof PROGRESS_STEPS)[number], string>> = {
  prepare: 'подготовка',
  analyze: 'разбор',
  decide: 'решение',
  reply: 'ответ',
  checks: 'проверка',
  pacing: 'темп',
};

// ───────────────────────────── метки хода ─────────────────────────────

/** Метки хода (UX §3): «Ход упал» с причиной, «готовится» с шагом — функции; остальные — готовые строки. */
export const TURN_STATUS_RU = {
  draft: 'Черновик · клиенту не ушёл',
  sent: '✓ Отправлено',
  human_sent: '✓ Отправил человек по черновику',
  human_sent_edited: '✓ Отправил человек по черновику (с правкой)',
  failed: (failureRu: string): string => `● Ход упал: ${failureRu}`,
  pending: (no: number, stepRu: string | null): string => (stepRu === null ? `Ход ${String(no)} готовится` : `Ход ${String(no)} готовится: ${stepRu}`),
} as const;

/** «Поймано проверкой»: что нашла проверка и чем кончилось — переписала или нейтральный ответ. */
export const CAUGHT_RU = {
  rewritten: (reasonRu: string): string => `● Поймано проверкой: ${reasonRu} — переписала`,
  neutral: (reasonRu: string): string => `● Поймано проверкой: ${reasonRu} — нейтральный ответ и передача коллеге`,
  /** Нейтральный ответ со звонком по номеру (Ruling 131): передачи нет, номер — менеджеру. */
  neutralCall: (reasonRu: string): string => `● Поймано проверкой: ${reasonRu} — номер менеджеру, ответ шаблоном`,
  /**
   * Ответ кода вместо вариантов модели (Q18): заявка в продажи или следующий вопрос, передачи нет. Только текст строки
   * `caughtRu` — вид ответа сервера тот же, старый пульт в открытой вкладке её показывает как есть.
   */
  codeReply: (reasonRu: string, whatRu: string): string => `● Поймано проверкой: ${reasonRu} — ответ кода: ${whatRu}`,
} as const;

/** Что показала проверка перед «отправкой» (`drafts.send_check`): время — «ЧЧ:ММ:СС». */
export const SEND_RU = {
  pending: (time: string): string => `Отправила бы в ${time} МСК`,
  would_send: (time: string): string => `Ушло бы клиенту в ${time} МСК`,
  dropped_newer: 'Не ушло бы: клиент написал ещё',
  dropped_human: 'Не нужно: сотрудник ответил раньше',
  dropped_off: 'Не ушло бы: Анна выключена',
  // Бой (L4): итог отправки частями через ai-reply.
  sent: 'Ушло клиенту',
  sent_partial: 'Ушло клиенту не целиком',
  send_failed: 'Не ушло: Омнидеск не принял или не доставил ответ',
  dropped_closed: 'Не ушло: чат закрыт',
  dropped_window: 'Не ушло: окно WhatsApp закрыто, чат передан людям',
} as const;

/**
 * Подписи автора сообщения; у сотрудника — его короткое имя («Андрей П.»). «✦ Анна · ИИ» — её ответ в бою через ai-reply
 * (`messages."aiAgent" = 'ai-qual'`, L1), так же, как метка исходящего в Омнидеске (F5): просто «Анна» можно спутать с
 * живой сотрудницей. «Старый бот» — ИИ-ответ без `aiAgent`.
 */
export const AUTHOR_RU = {
  client: 'Клиент',
  ai_qual: '✦ Анна · ИИ',
  old_ai: 'Старый бот',
  broadcast: 'Рассылка',
  staff: 'Сотрудник',
} as const;

// ───────────────────────────── нарушения проверки ─────────────────────────────

/** Коды нарушений проверки ответа (A12, `internal_ref` из C1-12, `avito_contacts` — Ruling 128): подписи для замечаний проверки. */
export const VIOLATION_RU: Readonly<Record<string, string>> = {
  number_not_grounded: 'Число без опоры',
  percent: 'Проценты',
  minimum_sum: 'Минимальная сумма',
  cargo: 'Слово „карго“',
  cash_card_crypto: 'Наличные, карта или криптовалюта',
  inn_question: 'Вопрос про ИНН',
  staff_name: 'Имя сотрудника',
  link: 'Ссылка вне белого списка',
  phone: 'Телефон вне белого списка',
  avito_contacts: 'Телефон, почта или ссылка в чате Авито',
  too_many_questions: 'Один вопрос за раз',
  question_not_last: 'Вопрос не в конце',
  // План A22, итоговое ревью, I2: решение кода велело сказать утверждённую фразу, а её в ответе нет (режим «по брифу»).
  approved_phrase_missing: 'Нет утверждённой фразы слово в слово',
  // План A25, волна 1: «как сотрудник» решение велело спросить о пороге, а в ответе нет порога цифрой («200 тыс. ₽», «750 кг»
  // или «5 м³», «30 000 юаней» — итоговое ревью A25, M8) или вопроса.
  threshold_element_missing: 'Нет порога цифрой или вопроса',
  part_too_long: `Часть длиннее 1${THIN}000 знаков`,
  avito_total_too_long: `Авито: больше 2${THIN}000 знаков`,
  markdown: 'Разметка Markdown',
  emoji: 'Эмодзи',
  robot_phrase_missing: 'Нет фразы про цифрового помощника',
  parts_count: 'Частей не от одной до трёх',
  internal_ref: 'Внутренний номер или служебное слово',
  self_blame: 'Извинение за прошлый ответ',
  vague_threshold: 'Минимум без суммы',
  // Задача AN1: чат не покупателя закрывается, а ответ обещает передачу или ответ коллеги.
  handoff_promise: 'Обещание передачи, а чат закрывается',
  // Задача AN3: вопрос о формате или о пороге партии второй раз, хотя клиент уже ответил.
  repeated_filter: 'Вопрос-фильтр второй раз',
  // План A21, волна 2: пересказ слов клиента в начале ответа (мягкое нарушение) и валюта к сумме клиента без его слов.
  client_echo: 'Пересказ слов клиента',
  currency_invented: 'Валюта, которую клиент не называл',
};

/** Замечания оценщика («судьи») и модерации. */
export const JUDGE_RU = {
  not_answered: 'Оценщик: не ответила на вопрос',
  unsupported: 'Оценщик: утверждение без опоры',
  template: 'Оценщик: шаблонно',
  // План A25, волна 1 (судья «как сотрудник»; итоговое ревью A25, M6): мягкий брак, повод переписать.
  ignored: 'Оценщик: мимо сказанного',
  repeated: 'Оценщик: повтор',
  asked_known: 'Оценщик: переспросила известное',
  moderation: 'Модерация: ответ отклонён',
} as const;

// ───────────────────────────── стоимость, инструменты, стоп-лист ─────────────────────────────

/** Группы расхода в «Стоимости» и функции модели, которые в них входят (`turn_spend.func`, C1-1). */
export const SPEND_GROUPS = ['replies', 'analysis', 'checks', 'media', 'links', 'research', 'lessons', 'other'] as const;
export type SpendGroup = (typeof SPEND_GROUPS)[number];

export const SPEND_GROUP_RU: Readonly<Record<SpendGroup, string>> = {
  replies: 'ответы',
  analysis: 'разбор',
  checks: 'проверки',
  media: 'голос, фото, файлы',
  links: 'ссылки',
  research: 'исследователь',
  lessons: 'уроки',
  other: 'прочее',
};

/** В какую группу идёт функция расхода; чего нет в таблице — «прочее». */
export const SPEND_FUNC_GROUP: Readonly<Record<string, SpendGroup>> = {
  reply: 'replies',
  rewrite: 'replies',
  followup: 'replies',
  analyze: 'analysis',
  mail_triage: 'analysis',
  judge: 'checks',
  moderation: 'checks',
  transcribe: 'media',
  vision: 'media',
  file_read: 'media',
  inspect: 'links',
  research: 'research',
  lesson: 'lessons',
};

/** Инструменты хода (C1-10, C2, P1) — по порядку реестра. */
export const TOOL_NAMES = [
  'kb_search',
  'stoplist_check',
  'product_inspect',
  'research_lookup',
  'money_convert',
  'company_lookup',
  'lead_update',
  'lead_to_sales',
  'lead_reject',
  'human_handoff',
  'call_request',
  'call_schedule',
  'followup_schedule',
] as const;
export type ToolName = (typeof TOOL_NAMES)[number];

export const TOOL_RU: Readonly<Record<ToolName, string>> = {
  kb_search: 'поиск по базе',
  stoplist_check: 'стоп-лист',
  product_inspect: 'разбор ссылки',
  research_lookup: 'исследователь',
  money_convert: 'курс валют',
  company_lookup: 'проверка компании',
  lead_update: 'поля лида',
  lead_to_sales: 'в продажи',
  lead_reject: 'отказ',
  human_handoff: 'передача человеку',
  call_request: 'номер менеджеру',
  call_schedule: 'звонок',
  followup_schedule: 'напоминание',
};

/** Итоги стоп-листа (план B). */
export const STOPLIST_VERDICTS = ['allowed', 'needs_docs', 'unprofitable_small', 'no_supplier_search', 'forbidden', 'unknown'] as const;
export type StoplistVerdictCode = (typeof STOPLIST_VERDICTS)[number];

export const STOPLIST_VERDICT_RU: Readonly<Record<StoplistVerdictCode, string>> = {
  allowed: 'можно',
  needs_docs: 'нужны документы',
  unprofitable_small: `невыгодно малой партией`,
  no_supplier_search: 'без поиска поставщика',
  forbidden: 'не возим',
  unknown: 'спорный — к человеку',
};

export const STOPLIST_VERDICT_TONE: Readonly<Record<StoplistVerdictCode, StatusTone>> = {
  allowed: 'sales',
  needs_docs: 'human',
  unprofitable_small: 'human',
  no_supplier_search: 'human',
  forbidden: 'reject',
  unknown: 'human',
};

// ───────────────────────────── шапка и лента ─────────────────────────────

/** Шаг идущего хода: номер хода среди ходов без заменённых и шаг словами (`null` — шаг ещё не записан). */
export const ProgressSchema = z.object({ turnNo: z.number().int().positive(), stepRu: z.string().nullable() });
export type Progress = z.infer<typeof ProgressSchema>;

/**
 * Шапка разбора. Телефон: admin видит как в Омнидеске, остальные — маской (`phoneHidden: true`). `dealUrl` — только адрес
 * портала Битрикса и номер сделки (код вебхука в ответ не попадает). `traceId` — только admin.
 */
export const DialogHeaderSchema = z.object({
  id: z.string().min(1),
  ticketNumber: z.number().int().positive().nullable(),
  customerLabel: z.string().min(1),
  phone: z.string().nullable(),
  phoneHidden: z.boolean(),
  channelLabel: z.string().min(1),
  mode: ModeSchema,
  outcome: OutcomeSchema,
  dealId: z.number().int().positive().nullable(),
  dealUrl: z.url().nullable(),
  omniUrl: z.url(),
  startedAt: MomentSchema,
  lastActivityAt: MomentSchema,
  turnsTotal: z.number().int().nonnegative(),
  reviewedTurns: z.number().int().nonnegative(),
  costRub: z.number().nonnegative(),
  firstReplySec: z.number().int().nonnegative().nullable(),
  /** Сколько ходов поймала проверка. */
  caught: z.number().int().nonnegative(),
  traceId: z.string().nullable(),
  /** Диалог идёт: клиент писал меньше 30 минут назад или есть ход в работе. */
  isLive: z.boolean(),
  progress: ProgressSchema.nullable(),
  /** Чат передал Анне сотрудник Омнидеска (план HO): момент передачи и кто («Андрей П.»); `null` — передачи не было. */
  handover: z.object({ at: MomentSchema, byRu: z.string().min(1) }).nullable(),
});
export type DialogHeader = z.infer<typeof DialogHeaderSchema>;

/** Заметка к сообщению: голосовое, фото, файл, ссылка — словами. */
export const NOTE_KINDS = ['voice', 'photo', 'file', 'link'] as const;
export const MessageNoteSchema = z.object({ kind: z.enum(NOTE_KINDS), textRu: z.string().min(1) });
export type MessageNote = z.infer<typeof MessageNoteSchema>;

/**
 * Кто написал сообщение: клиент, сотрудник, Анна (ответ в бою, класс `ai_qual` по `messages."aiAgent"`), старый бот,
 * рассылка (служебные сообщения в ленту не идут).
 */
export const TIMELINE_AUTHORS = ['client', 'staff', 'ai_qual', 'old_ai', 'broadcast'] as const;

export const MessageEntrySchema = z.object({
  kind: z.literal('message'),
  id: z.string().min(1),
  at: MomentSchema,
  author: z.enum(TIMELINE_AUTHORS),
  authorRu: z.string().min(1),
  text: z.string(),
  notes: z.array(MessageNoteSchema),
});

export type MessageEntry = z.infer<typeof MessageEntrySchema>;

/** Группа «Пути»: глагол и строки. */
export const PathGroupSchema = z.object({ verbRu: z.string().min(1), lines: z.array(z.string().min(1)) });
export type PathGroup = z.infer<typeof PathGroupSchema>;

/** Состояние хода в ленте: черновик, отправлено, отправил человек, упал, готовится. */
export const TURN_ENTRY_STATUSES = ['draft', 'sent', 'human_sent', 'failed', 'pending'] as const;

export const TurnEntrySchema = z.object({
  kind: z.literal('turn'),
  turnId: z.string().min(1),
  /** Номер среди ходов без заменённых (`superseded`). */
  no: z.number().int().positive(),
  at: MomentSchema,
  /** Сколько секунд ход работал (от записи хода до черновика), до десятых; `null` — черновика нет. */
  durationSec: z.number().nonnegative().nullable(),
  costRub: z.number().nonnegative(),
  status: z.enum(TURN_ENTRY_STATUSES),
  labelRu: z.string().min(1),
  caughtRu: z.string().nullable(),
  parts: z.array(z.string()),
  /** Первый вариант, который отклонила проверка: части и причина. */
  rejected: z.object({ parts: z.array(z.string()), reasonRu: z.string() }).nullable(),
  path: z.array(PathGroupSchema),
  sendRu: z.string().nullable(),
  humanReply: z.object({ text: z.string(), staffRu: z.string(), at: MomentSchema, matchRu: z.string().nullable() }).nullable(),
  kbIds: z.array(z.string()),
  firstQuestion: z.string().nullable(),
  traceId: z.string().nullable(),
  review: TurnReviewViewSchema.nullable(),
  exampleSaved: z.boolean(),
  problem: z.boolean(),
  /**
   * Попытки, где судья вынес вердикт (план M, M2): по каждому признаку — что он сказал и моя метка «судья прав / ошибся»
   * (`mine`; у «Просмотра» — всегда `null`). Ответ сервера до M2 поля не знает — пусто.
   */
  judge: z.array(JudgeAttemptViewSchema).default([]),
});
export type TurnEntry = z.infer<typeof TurnEntrySchema>;

/** Ход, пропущенный воротами: системная строка «Анна промолчала: …». */
export const SkipEntrySchema = z.object({
  kind: z.literal('skip'),
  turnId: z.string().min(1),
  at: MomentSchema,
  textRu: z.string().min(1),
  reason: GateReasonSchema.nullable(),
});
export type SkipEntry = z.infer<typeof SkipEntrySchema>;

export const TimelineEntrySchema = z.discriminatedUnion('kind', [MessageEntrySchema, TurnEntrySchema, SkipEntrySchema]);
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;

/**
 * Разбор диалога: шапка, лента по времени, ход в фокусе (`?turn=`, а без него — первый проблемный; `null` — не на чём
 * фокусироваться), момент ответа и курс, по которому доллары пересчитаны в рубли.
 */
export const DialogViewSchema = z.object({
  header: DialogHeaderSchema,
  timeline: z.array(TimelineEntrySchema),
  focusTurnNo: z.number().int().positive().nullable(),
  at: MomentSchema,
  fx: FxSchema,
});
export type DialogView = z.infer<typeof DialogViewSchema>;

// ───────────────────────────── правая колонка ─────────────────────────────

/** Поле лида с основанием: ход и сообщение, из которых оно взято, и цитата клиента. */
export const LeadFieldViewSchema = z.object({
  key: LeadKeySchema,
  labelRu: z.string().min(1),
  value: z.string(),
  sourceRu: z.string().min(1),
  turnNo: z.number().int().positive().nullable(),
  messageId: z.string().nullable(),
  quote: z.string().nullable(),
});
export type LeadFieldView = z.infer<typeof LeadFieldViewSchema>;

export const DialogSummarySchema = z.object({
  outcome: OutcomeSchema,
  /** Почему так решил код (`decision.reasons` последнего хода с решением). */
  whyRu: z.array(z.string()),
  /** Чего не хватает лиду (`decision.missing`) словами. */
  missingRu: z.array(z.string()),
  lead: z.array(LeadFieldViewSchema),
  stoplist: z.object({ verdictRu: z.string(), tone: StatusToneSchema, productsRu: z.array(z.string()) }).nullable(),
  /** Оценщик: сколько «да» из скольких и первая неудача (ход и замечание). */
  grades: z.object({
    yes: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    firstFail: z.object({ labelRu: z.string(), turnNo: z.number().int().positive() }).nullable(),
  }),
  cost: z.object({
    totalRub: z.number().nonnegative(),
    byFunc: z.array(z.object({ labelRu: z.string(), rub: z.number().nonnegative() })),
  }),
  tools: z.array(z.object({ labelRu: z.string(), count: z.number().int().positive() })),
  /** Трасса последнего хода: только admin; ссылка на Langfuse — если заданы адрес и проект. */
  trace: z.object({ id: z.string().min(1), langfuseUrl: z.string().nullable() }).nullable(),
  at: MomentSchema,
  fx: FxSchema,
});
export type DialogSummary = z.infer<typeof DialogSummarySchema>;

// ───────────────────────────── разбор одного хода (C1-13) ─────────────────────────────

/** Строка «Пути» (`PathStep`, C1-13): шаг и текст. Разбор диалога отдаёт их группами (`PathGroup`), разбор хода — как есть. */
export const PathStepEntrySchema = z.object({ step: PathStepSchema, textRu: z.string().min(1) });
export type PathStepEntry = z.infer<typeof PathStepEntrySchema>;

/** Строка хода как в БД (`turns`): JSON-колонки — без разбора, моменты — ISO UTC. */
export const TurnRowSchema = z.object({
  id: z.string().min(1),
  dialogId: z.string().min(1),
  jobId: z.string().nullable(),
  triggerMsgId: z.string().nullable(),
  status: z.string().min(1),
  gateReason: z.string().nullable(),
  decision: z.unknown(),
  parts: z.unknown(),
  checks: z.unknown(),
  costUsd: z.number().nonnegative(),
  latencyMs: z.unknown(),
  langfuseTraceId: z.string().nullable(),
  releaseId: z.string(),
  analysis: z.unknown(),
  contextRefs: z.unknown(),
  effort: z.string().nullable(),
  promptVersion: z.string().nullable(),
  usage: z.unknown(),
  error: z.string().nullable(),
  progress: z.string().nullable(),
  createdAt: MomentSchema,
  updatedAt: MomentSchema,
});
export type TurnRowView = z.infer<typeof TurnRowSchema>;

/** Черновик хода как в БД (`drafts`). */
export const DraftRowSchema = z.object({
  id: z.string().min(1),
  turnId: z.string().min(1),
  dialogId: z.string().min(1),
  parts: z.array(z.string()),
  source: z.string().min(1),
  usedKbIds: z.array(z.string()),
  usedFactIds: z.array(z.string()),
  wouldSendAt: MomentSchema,
  isFirstReply: z.boolean(),
  firstReplyLate: z.boolean(),
  sendCheck: z.string().min(1),
  checkedAt: MomentSchema.nullable(),
  createdAt: MomentSchema,
});
export type DraftRowView = z.infer<typeof DraftRowSchema>;

/**
 * Разбор одного хода (`GET /api/v1/turns/:id`, только admin): сырые строки хода и черновика (в них может быть телефон
 * клиента — поэтому «Просмотру» маршрут закрыт), «Путь» строками и ссылка на трассу в Langfuse — только когда заданы адрес
 * Langfuse, проект и у хода есть трасса. Пульт этот маршрут не вызывает: «Путь» приходит в ленте разбора диалога.
 */
export const TurnDetailSchema = z.object({
  turn: TurnRowSchema,
  draft: DraftRowSchema.nullable(),
  path: z.array(PathStepEntrySchema),
  langfuseUrl: z.string().nullable(),
});
export type TurnDetail = z.infer<typeof TurnDetailSchema>;
